import React, { useState, Dispatch, SetStateAction } from 'react';
import { TourName } from 'js/sign-components/common/TourName';
import { createSimpleDataContext } from 'js/sign-components/common/create-simple-data-context';

type ToursContextShape = {
  activeTour: TourName | null;
  pendingTourChecks: Partial<Record<TourName, number>>;
  beginTourCheck: (tour: TourName) => () => void;
  setActiveTour: Dispatch<SetStateAction<TourName | null>>;
  addToCompletedTours: (tour: TourName) => void;
  completedTours: TourName[];
};

export const ToursContext =
  createSimpleDataContext<ToursContextShape>('Tours Context');

export type ToursContextProviderProps = React.PropsWithChildren<{
  completedTours: TourName[];
}>;

export function ToursProvider(props: ToursContextProviderProps) {
  const [activeTour, setActiveTour] = useState<TourName | null>(null);
  const [pendingTourChecks, setPendingTourChecks] = useState<
    Partial<Record<TourName, number>>
  >({});
  const beginTourCheck = React.useCallback((tour: TourName) => {
    setPendingTourChecks((checks) => ({
      ...checks,
      [tour]: (checks[tour] ?? 0) + 1,
    }));
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      setPendingTourChecks((checks) => ({
        ...checks,
        [tour]: (checks[tour] ?? 0) - 1,
      }));
    };
  }, []);
  /**
   * When we send a request to complete a tour
   */
  const [completedTours, setCompletedTours] = useState<TourName[]>(
    props.completedTours,
  );

  const addToCompletedTours = React.useCallback((tour: TourName) => {
    setCompletedTours((tours) => {
      if (tours.includes(tour)) {
        return tours;
      }
      return [...tours, tour];
    });
  }, []);
  return (
    <ToursContext.Provider
      value={{
        activeTour,
        pendingTourChecks,
        beginTourCheck,
        setActiveTour,
        addToCompletedTours,
        completedTours,
      }}
    >
      {props.children}
    </ToursContext.Provider>
  );
}
